import React, { useEffect, useReducer, useState } from "react";
import { useAppSelector, useAppDispatch } from "@/services/hooks";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import { useCurrentSite } from "@/services/siteServices/useCurrentSite";
import { ToastContainer } from "react-toastify";
import NotificationAlertOptions from "@/layouts/Alerts";
import {
  getPosts,
  getBlog,
  addPost,
  clearMessage,
} from "@/services/blogServices/blogActions";
import { getCurrentProfile } from "@/services/userServices/profileAction";
import { useImageUpload } from "@/services/ImageServices/useImageUpload";
import { useBlockImageUpload } from "@/services/ImageServices/useBlockImageUpload";
import CreatePostView from "./createpostView";
import { postForm } from "./postForm";
import { altOf } from "@/views/pages/components/Media/ModelMediaContent/ImageInformation";
import { asCaught } from "@/services/caughtError";

const formActions = postForm.actions;

function CreatePostContainer() {
  const dispatch = useAppDispatch();
  const uploadImage = useImageUpload();
  const uploadBlockImage = useBlockImageUpload();

  const blog = useAppSelector((s) => s.blog) as any;
  const errors = useAppSelector((s) => s.errors) as any;
  const permission = useCurrentPermission();
  const currentsite = useCurrentSite();

  // SCRUM-2260: the post draft (title, type, content, picture, blocks, byline) is one
  // createSlice, mounted locally; the country code is read from the site in render.
  const [form, dispatchForm] = useReducer(postForm.reducer, postForm.getInitialState());
  const { blocks, data, reactQuillText, picture, title, reset, type, editor, reviewer } = form;
  const currentSiteCountryCode = currentsite?.country_code?.toLowerCase() ?? "";

  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState({ title: true, content: true });

  useEffect(() => {
    dispatch(getBlog());
    dispatch(getPosts());
    dispatch(getCurrentProfile());

  }, []);


  useEffect(() => {
    if (errors?.errors?.length) {
      const next = { ...validation };
      errors.errors.forEach((e) => {
        if (e?.param && e?.value === "") next[e.param] = false;
      });
      setValidation(next);
      setLoading(false);
    }
   
  }, [errors]);


  useEffect(() => {
    if (blog?.message) {
      NotificationAlertOptions("success", blog.message);
      dispatch(clearMessage());
      setLoading(false);
      dispatchForm(formActions.resetCleared());
    }

  }, [blog?.message]);


  const onChange = (e) => {
    const { name, value } = e.target;
    if (name === "title") {
      dispatchForm(formActions.titleChanged(value));
      setValidation((v) => ({ ...v, title: true }));
    }
  };

  const onChangeToggle = (e) => {
    const { name, checked } = e.target;
    if (name === "type") dispatchForm(formActions.typeChanged(checked));
  };

  const handleReactQuillChange = (value) => {
    dispatchForm(formActions.contentChanged(value));
    setValidation((v) => ({ ...v, content: true }));
  };

  const ImageUploadRecievedHandler = (img) => dispatchForm(formActions.pictureReceived(img));

  const isEmptyBlock = (b) =>
    (b?.title || "") === "" &&
    (b?.description || "") === "" &&
    (b?.image || "") === "";

  const handleAdd = (newArr) => dispatchForm(formActions.blocksSet(newArr));
  const handleDelete = (newArr) => dispatchForm(formActions.blocksSet(newArr));

  const handleBlockArray = async (blockContentArray) => {
    const prevBlocks = blocks;

    if (!blockContentArray) return;

    const processed = await Promise.all(
      blockContentArray
        .filter((b) => !isEmptyBlock(b))
        .map(async (block, index) => {
        
          if (block?.image?.data) {
     
            if (!prevBlocks[index] || prevBlocks[index].image !== block.image) {
              const path = `/${currentSiteCountryCode}/POSTS/${block.image.imgName}`;
              // Linearised (SCRUM-1021): uploadImagesAction resolves to the
              // uploaded URL, so set it on the block inline instead of the old
              // uploadedImages watch-effect that merged it back by index. A
              // failed upload re-throws a plain Error (SCRUM-815); surface it
              // per block instead of rejecting the whole Promise.all silently.
              try {
                const url = await uploadBlockImage(
                  block.index,
                  block.image.data,
                  path
                );
                return { ...block, image: url };
              } catch {
                NotificationAlertOptions(
                  "error",
                  `Error uploading image in block ${block.index + 1}`
                );
                return { ...block, image: block.image };
              }
            }
            return block;
          }
          
          if (block?.image?.secure_url) {
            return { ...block, image: block.image.secure_url };
          }
          // Preserve an already-uploaded URL string (or any existing string
          // image); resetting to "" here dropped the block's uploaded URL on a
          // later handleBlockArray call (SCRUM-1021). Mirrors UpdatePostContainer.
          return { ...block, image: block?.image || "" };
        })
    );

    // Feed the processed blocks (with uploaded URLs) back to the editor — not
    // the raw {data} — so a later handleBlockArray call (add/edit a block) sees
    // the URL and preserves it, instead of skip-reverting to the {data} object
    // the backend rejects ("Cast to string failed for blocks.0.image"). This is
    // what the removed uploadedImages watch-effect used to guarantee by merging
    // URLs back by index. (SCRUM-1021)
    dispatchForm(formActions.blocksProcessed(processed));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const postData = {
      ...data,
      type,
      title,
      content: reactQuillText.split("<").join("&lt;").split(">").join("&gt;"),
      pathpost: `blog/${title}`,
      // Neither `idUser` nor `pathauthor` is sent any more (SCRUM-1011 /
      // SCRUM-1064): the server derives both from the authenticated caller,
      // so sending them was at best redundant and at worst a spoofing vector.
      idBlog: blog?._id,
      // SCRUM-1918: the chosen editor/reviewer as user ids; "none" sends nothing.
      ...(editor ? { editor } : {}),
      ...(reviewer ? { reviewer } : {}),
    };

    dispatchForm(formActions.submitted(postData));


    if (picture?.data) {
      const path = `/${currentSiteCountryCode}/POSTS/${picture.imgName}`;
      setLoading(true);
      // Linearised (SCRUM-1021): upload via useImageUpload, then save inline
      // with the returned URL — no more url watch-effect (the old success-only
      // effect silently dropped the post on a failed upload). Bail before the
      // field reset below so the user keeps their input on failure.
      try {
        const pictureUrl = await uploadImage(picture.data, path);
        // SCRUM-1595: a freshly uploaded file has no alt the form knows of.
        dispatch(addPost({ ...postData, picture: pictureUrl, picture_alt: "" }));
      } catch (err) {
        setLoading(false);
        NotificationAlertOptions(
          "error",
          asCaught(err)?.message || "Uploading the cover image failed. Please try again."
        );
        return;
      }
    }

    else if (picture?.secure_url !== undefined) {
      // SCRUM-1595: a picture picked from the media library brings the alt
      // stored on the resource (SCRUM-1593 / 1594) as the post's picture_alt.
      dispatch(
        addPost({ ...postData, picture: picture.secure_url, picture_alt: altOf(picture) })
      );
    } else {

      dispatch(addPost(postData));
    }

    // Below the failure branch's return, so a failed upload keeps the input (SCRUM-2281).
    dispatchForm(formActions.postSent());
  };

  return (
    <>
      <div className="rna-wrapper">
        <ToastContainer />
      </div>

      <CreatePostView
     
        permission={permission || {}}

        title={title}
        type={type}
        validation={validation}
        reactQuillText={reactQuillText}
        reset={reset}
        blocks={blocks}
        data={data}
        editor={editor}
        reviewer={reviewer}

        onChange={onChange}
        onEditorChange={(id) => dispatchForm(formActions.editorChanged(id))}
        onReviewerChange={(id) => dispatchForm(formActions.reviewerChanged(id))}
        onChangeToggle={onChangeToggle}
        handleReactQuillChange={handleReactQuillChange}
        ImageUploadRecievedHandler={ImageUploadRecievedHandler}
        handleAdd={handleAdd}
        handleDelete={handleDelete}
        handleBlockArray={handleBlockArray}
        onSubmit={onSubmit}
      />
    </>
  );
}

export default CreatePostContainer;
